import { ComponentRepresentation } from '@ardoq/basic-components';
import { RepresentationData } from '@ardoq/data-model';
import { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { CUSTOM_SVG_ICON_SELECTOR, IconSize } from '@ardoq/icons';
import { classes } from '@ardoq/common-helpers';
import { EXPORT_AUTOSIZE_CLASS_NAME } from '@ardoq/global-consts';
import { BULK_POPOVER_ATTR, PlainTextPopover } from '@ardoq/popovers';

const COMPONENT_LABEL_TEXT_ATTR = 'data-component-label-popover';

export const resolveComponentLabelPopover = (
  element: HTMLElement | SVGElement
): ReactNode | null => {
  const text = element.getAttribute(COMPONENT_LABEL_TEXT_ATTR);
  if (!text) return null;
  return <PlainTextPopover>{text}</PlainTextPopover>;
};

const ICON_MARGIN = 8;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 ${ICON_MARGIN}px 0 0;
  height: 18px;
  min-width: 18px;
  font-size: 18px;
  text-align: center;
  max-width: min-content;

  > ${CUSTOM_SVG_ICON_SELECTOR} {
    width: -webkit-fill-available;
  }
`;

const TextContainer = styled.span<{ $isWidthSmallerThanVisible?: boolean }>`
  flex: 1;
  ${({ $isWidthSmallerThanVisible }) =>
    $isWidthSmallerThanVisible
      ? css`
          word-break: break-all;
        `
      : css`
          overflow: hidden;
          text-overflow: ellipsis;
        `}
`;

type ComponentLabelProperties = {
  representationData?: RepresentationData | null;
  label?: string;
  labelClassName?: string;
  iconClassName?: string;
  iconColor?: string;
  isWidthSmallerThanVisible?: boolean;
};

export const ComponentLabel = ({
  representationData,
  label,
  labelClassName,
  iconClassName,
  iconColor,
  isWidthSmallerThanVisible,
}: ComponentLabelProperties) => (
  <>
    {representationData && (
      <IconContainer className={iconClassName}>
        <ComponentRepresentation
          isImage={representationData.isImage}
          value={representationData.value}
          icon={representationData.icon}
          style={{ color: iconColor, stroke: iconColor }}
          iconSize={18 as IconSize}
        />
      </IconContainer>
    )}
    <TextContainer
      $isWidthSmallerThanVisible={isWidthSmallerThanVisible}
      className={classes(labelClassName, EXPORT_AUTOSIZE_CLASS_NAME)}
      {...(label
        ? { [BULK_POPOVER_ATTR]: 'true', [COMPONENT_LABEL_TEXT_ATTR]: label }
        : {})}
    >
      {label?.split('\n').map(line => (
        <div key={line}>{line}</div>
      ))}
    </TextContainer>
  </>
);
